import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AxiosResponse } from "axios";
import { useDispatch, useSelector } from "react-redux";

import {
    opusLanguages,
    folksongClasses,
    generalGenres,
    planTypes,
    exerciseTypes,
    folksongTempos,
    levels,
    taskTypes,
    eras,
} from "../../../../common/parameters";

import { get } from "../../../actions/general";
import { toggleFilterCategory } from "../../../store/browse/actions";
import { IStateTree } from "../../../store/interfaces";
import Icon from "../../../icon";
import { CheckboxThreeWay, Checkbox, Select, InputSelectCard, Label } from "../../../components/Form";
import ComposerCard from "../../../components/ComposerCard";
import GenreCard from "../../../components/GenreCard";
import { IComposerResponse, IGenre } from "../../plan/Meta/Opus/interfaces";

import getFiltersValue from "../getFiltersValue";
import { Slider, Arrow } from "../commonStyles";
import { IProps } from "../Pitch/interfaces";
import messages from "./messages";
import { IComposerData } from "./interfaces";
import { formatComposerName } from "../../../../common/helpers/formatComposerName";

const getComposers = (composers: IComposerData[], era: string) => {
    if (!era) {
        return composers;
    }
    return composers.filter(composer => composer.era === era);
}
const Opus = ({ filters, change }: IProps) => {
    const dispatch = useDispatch();
    const { formatMessage, locale } = useIntl();
    const [composers, setComposers] = useState<IComposerData[]>([]);
    const [genres, setGenres] = useState<IGenre[]>([]);
    const isOpen = useSelector((state: IStateTree) => state.browse.filterCategories.opus);
    const handleToggleCategory = () => {
        dispatch(toggleFilterCategory("opus"));
    }
    useEffect(() => {
        dispatch(get("composer-list", (res: AxiosResponse<{ composers: IComposerResponse[] }>) => {
            const rawData = res.data.composers;
            const composersData: IComposerData[] = [];
            rawData.forEach(data => {
                composersData.push({
                    label: formatComposerName(data.name, locale),
                    id: data.id,
                    era: data.era,
                    card: (
                        <ComposerCard
                            img={data.img}
                            name={formatComposerName(data.short!, locale)}
                            birth={data.birth}
                            death={data.death}
                            period={data.era}
                            country={data.country}
                            small
                        />
                    ),
                });
            });
            setComposers(composersData);
        }));

        const genresData = generalGenres(formatMessage).map(genre => {
            return {
                label: genre.label,
                id: genre.value,
                card: <GenreCard img={genre.img} name={genre.label} details={genre.details} small />,
            };
        });
        setGenres(genresData);
    }, []);
    return (
        <div>
            <h3 onClick={handleToggleCategory}>
                <FormattedMessage {...messages.opus} />
                <Arrow opened={isOpen}>
                    <Icon i="chevron-right" />
                </Arrow>
            </h3>
            {isOpen && (
                <Slider>
                    <div>
                        <Select
                            name="planType"
                            label={formatMessage(messages.type)}
                            value={getFiltersValue(filters, "opus.planType") as string}
                            options={planTypes(formatMessage)}
                            lightWithValue
                            onChange={e => {
                                change({
                                    target: { type: "select", value: e.target.value, name: "opus.planType" },
                                });
                            }}
                        />
                        {getFiltersValue(filters, "opus.planType") === "written" && (
                            <Select
                                name="era"
                                label={formatMessage(messages.era)}
                                value={getFiltersValue(filters, "opus.era") as string}
                                onChange={e => {
                                    change({
                                        target: {
                                            type: "select",
                                            value: e.target.value,
                                            name: "opus.era",
                                        },
                                    })
                                    change({
                                        target: {
                                            type: "select",
                                            value: undefined,
                                            name: "opus.composer",
                                        }
                                    })
                                }}
                                options={eras(formatMessage)}
                            />
                        )}
                        {getFiltersValue(filters, "opus.planType") === "folk" && (
                            <Select
                                lightWithValue
                                label={formatMessage(messages.folksongClass)}
                                name="opus.folksongClass"
                                value={getFiltersValue(filters, "opus.folksongClass") as string}
                                onChange={change}
                                options={folksongClasses(formatMessage)}
                            />
                        )}
                        {getFiltersValue(filters, "opus.planType") === "written" && (
                            <InputSelectCard
                                name="composer"
                                label={formatMessage(messages.composer)}
                                value={getFiltersValue(filters, "opus.composer") as string}
                                onChange={value =>
                                    change({ target: { type: "select", value, name: "opus.composer" } })
                                }
                                options={getComposers(composers, getFiltersValue(filters, "opus.era") as string)}
                            />
                        )}
                        {getFiltersValue(filters, "opus.planType") === "written" && (
                            <InputSelectCard
                                name="genre"
                                label={formatMessage(messages.genre)}
                                value={getFiltersValue(filters, "opus.genre") as string}
                                onChange={value =>
                                    change({ target: { type: "select", value, name: "opus.genre" } })
                                }
                                options={genres}
                            />
                        )}
                        {getFiltersValue(filters, "opus.planType") === "exercise" && (
                            <Select
                                name="exerciseType"
                                label={formatMessage(messages.exerciseType)}
                                value={getFiltersValue(filters, "opus.exerciseType") as string}
                                onChange={e =>
                                    change({
                                        target: {
                                            type: "select",
                                            value: e.target.value,
                                            name: "opus.exerciseType",
                                        },
                                    })
                                }
                                options={exerciseTypes(formatMessage)}
                            />
                        )}
                        {getFiltersValue(filters, "opus.planType") === "folk" && (
                            <Select
                                name="folksongTempo"
                                label={formatMessage(messages.folksongTempo)}
                                value={getFiltersValue(filters, "opus.folksongTempo") as string}
                                options={folksongTempos(formatMessage)}
                                onChange={e =>
                                    change({
                                        target: {
                                            type: "select",
                                            value: e.target.value,
                                            name: "opus.folksongTempo",
                                        },
                                    })
                                }
                            />
                        )}
                        <CheckboxThreeWay
                            label={formatMessage(messages.isChildrenSong)}
                            name="opus.isChildrenSong"
                            checked={getFiltersValue(filters, "opus.isChildrenSong") as boolean}
                            onChange={change}
                        />
                        <CheckboxThreeWay
                            label={formatMessage(messages.isChurchMusic)}
                            name="opus.isChurchMusic"
                            checked={getFiltersValue(filters, "opus.isChurchMusic") as boolean}
                            onChange={change}
                        />
                        <CheckboxThreeWay
                            label={formatMessage(messages.isLyrics)}
                            name="opus.hasLyrics"
                            checked={getFiltersValue(filters, "opus.hasLyrics") as boolean}
                            onChange={change}
                        />
                        <Select
                            lightWithValue
                            label={formatMessage(messages.language)}
                            name="opus.lyricLanguage"
                            value={getFiltersValue(filters, "opus.lyricLanguage") as string}
                            onChange={change}
                            options={opusLanguages(formatMessage)}
                        />
                        <CheckboxThreeWay
                            label={formatMessage(messages.isCanon)}
                            name="opus.canon"
                            checked={getFiltersValue(filters, "opus.canon") as boolean}
                            onChange={change}
                        />

                        <Select
                            lightWithValue
                            label={formatMessage(messages.level)}
                            tooltip={formatMessage(messages.levelTooltip)}
                            name="teaching.level"
                            value={getFiltersValue(filters, "teaching.level") as string}
                            onChange={change}
                            options={levels(formatMessage)}
                        />

                        <Checkbox
                            label={formatMessage(messages.experience)}
                            name="isExperience"
                            checked={getFiltersValue(filters, "isExperience") as boolean}
                            onChange={change}
                        />

                        <Label>
                            <FormattedMessage {...messages.taskTypes} />
                        </Label>
                        {Object.keys(taskTypes(formatMessage)).map(type => {
                            return (
                                <CheckboxThreeWay
                                    key={type}
                                    label={taskTypes(formatMessage)[type]}
                                    name={"tasks.type." + type}
                                    checked={getFiltersValue(filters, "tasks.type." + type) as boolean}
                                    onChange={change}
                                ></CheckboxThreeWay>
                            );
                        })}
                    </div>
                </Slider>
            )}
        </div>
    );
}

export default Opus;
